'use client';

import type { Life, Note, Post } from 'content-collections';

// import { useTranslations } from '@tszhong0411/i18n/client'
import { Badge, BlurImage } from '@mcdougald/ui/src';
import { useQuery } from '@tanstack/react-query';

import { useFormattedDate } from '@/hooks/use-format-date';
import { useTRPC } from '@/trpc/client';

import Link from './link';

type PostCardsProps = {
  posts: Post[];
  notes: Note[];
  lifeUpdates?: Life[];
};

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
type PostCardProps = Post | Note | Life;

type CardProps = {
  data: PostCardProps;
  imageSrc: string;
};

const PostCards = (props: PostCardsProps) => {
  const { posts, notes, lifeUpdates } = props;

  return (
    <div className="grid gap-4 md:grid-cols-4 px-5">
      {posts.map((post) => (
        <PostCard
          key={post.slug}
          data={{ ...post }}
          imageSrc={'/images/blogs/Blog.png'}
        />
      ))}
      {notes.map((note) => (
        <PostCard
          key={note.slug}
          data={{ ...note }}
          imageSrc={'/images/blogs/Notes.png'}
        />
      ))}
      {lifeUpdates?.map((lifeUpdate) => (
        <PostCard
          key={lifeUpdate.slug}
          data={{ ...lifeUpdate }}
          imageSrc={'/images/blogs/LifeUpdate.png'}
        />
      ))}
    </div>
  );
};

const PostCard = (props: CardProps) => {
  const { slug, title, summary, date, categories } = props.data;
  const formattedDate = useFormattedDate(String(date));
  const trpc = useTRPC();

  const viewsQuery = useQuery(
    trpc.views.get.queryOptions({ slug: String(slug) }),
  );
  const likesQuery = useQuery(
    trpc.likes.get.queryOptions({ slug: String(slug) }),
  );

  return (
    <Link
      href={`/posts/${slug}`}
      className="shadow-feature-card bg-[#151515]  group rounded-xl px-2 py-4 flex flex-col "
    >
      <BlurImage
        //src={`/images/blogs/${slug}/cover.png`}
        src={props.imageSrc}
        className="rounded-lg"
        width={1200}
        height={630}
        imageClassName="transition-transform group-hover:scale-105"
        alt={title}
      />
      <div className="flex items-center justify-between gap-2 px-2 pt-4 text-sm text-zinc-500">
        {formattedDate}
        <div className="flex gap-2">
          {likesQuery.status === 'pending' ? '--' : null}
          {likesQuery.status === 'error' ? 'Error' : null}
          {likesQuery.status === 'success' ? (
            <div>{'Likes ' + String(likesQuery.data.likes)}</div>
          ) : null}
          <div>&middot;</div>
          {viewsQuery.status === 'pending' ? '--' : null}
          {viewsQuery.status === 'error' ? 'Error' : null}
          {viewsQuery.status === 'success' ? (
            <div>{'Views ' + String(viewsQuery.data.views)}</div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col px-2 py-4">
        <h3 className="text-2xl font-semibold">{title}</h3>
        <p className="text-muted-foreground mt-2">{summary}</p>
      </div>
      <div className={'mt-auto'}>
        {categories.map((category, i) => {
          return (
            <Badge variant={'ghost'} key={`${category}-${title}-${i}`}>
              {category}
            </Badge>
          );
        })}
      </div>
    </Link>
  );
};

export default PostCards;
